import { createAsyncThunk } from "@reduxjs/toolkit";
import { getAsync, postAsync } from "../../../api/Api";
import { iframeDebug } from "../../../utils/iframeDebug";
import {
  MOCK_GET_STRINGS_RESPONSE,
  MOCK_GET_TS_AFFILIATES_RESPONSE,
  MOCK_GET_TS_AFFILIATES_RESPONSE_CLICKS,
  MOCK_GET_TS_INITIAL_DATA_RESPONSE,
  mockDataEnabled,
} from "../../../mockData/MockData";
import {
  TsInitialDataResponse,
  TranslationsResponse,
  ServerTranslationsResponse,
  TsAffiliatesResponse,
  TsAffiliatesResponseClicks,
  GetCreditGuardIframeUrlInput,
  GetCreditGuardIframeUrlResponse,
  InquireGCIframeResultsInput,
  InquireGCIframeResultsResponse,
} from "../../../utils/types";

interface GetAsyncParams {
  url: string;
  hideLoader?: boolean;
}

// TODO: Implement getAsync function - this should be your API call function

export const getStrings = createAsyncThunk<
  TranslationsResponse,
  boolean | undefined
>("appStore/getStrings", async (hideLoader?: boolean) => {
  const data: GetAsyncParams = {
    url: "/qry/settings/dictionary",
    hideLoader: hideLoader,
  };

  if (mockDataEnabled) {
    return MOCK_GET_STRINGS_RESPONSE;
  }

  const response = (await getAsync(data)) as ServerTranslationsResponse;
  // Merge mock data with server response
  // Server response overrides mock data for matching keys
  const mergedResponse: TranslationsResponse = {
    responseMessage: "",
    responseCode: "",
    responseData: {
      strings: {
        ...MOCK_GET_STRINGS_RESPONSE.responseData.strings,
        ...(response["he-IL"] || {}),
      },
    },
  };

  return mergedResponse;
});

// POST get-ts-initial-data
export const getTsInitialData = createAsyncThunk<TsInitialDataResponse>(
  "appStore/getTsInitialData",
  async () => {
    const data: any = {
      url: "/qry/settings/get-initial-data",
    };
    if (mockDataEnabled) {
      return MOCK_GET_TS_INITIAL_DATA_RESPONSE as TsInitialDataResponse;
    }

    const response = await getAsync(data);
    return response as TsInitialDataResponse;
  }
);

// POST get-ts-affiliates
export const getTsAffiliates = createAsyncThunk<TsAffiliatesResponse>(
  "appStore/getTsAffiliates",
  async () => {
    const data: any = {
      url: "/qry/settings/get-ts-affiliates",
    };
    if (mockDataEnabled) {
      return MOCK_GET_TS_AFFILIATES_RESPONSE as TsAffiliatesResponse;
    }
    const response = await getAsync(data);
    return response as TsAffiliatesResponse;
  }
);

// POST get-ts-affiliates-clicks
export const getTsAffiliatesClicks = createAsyncThunk<TsAffiliatesResponseClicks>(
  "appStore/getTsAffiliatesClicks",
  async () => {
    const data: any = {
      url: "/qry/settings/get-ts-affiliates-timers",
    };
    if (mockDataEnabled) {
      return MOCK_GET_TS_AFFILIATES_RESPONSE_CLICKS as TsAffiliatesResponseClicks;
    }
    const response = await getAsync(data);
    return response as TsAffiliatesResponseClicks;
  }
);
export const getCreditGuardIframeUrl = createAsyncThunk<
  GetCreditGuardIframeUrlResponse,
  GetCreditGuardIframeUrlInput
>("appStore/getCreditGuardIframeUrl", async (body: GetCreditGuardIframeUrlInput) => {
  const data = {
    url: "/cmd/credit-guard/get-iframe",
    body,
  };

  const response = (await postAsync(data)) as {
    url?: string;
    responseData?: { url?: string };
    failureRedirectUrl?: string;
  };
  const url = response.url ?? response.responseData?.url;
  if (!url) {
    const failureRedirectUrl = response.failureRedirectUrl;
    if (failureRedirectUrl) {
      return { failureRedirectUrl };
    }
    return Promise.reject(new Error("Credit Guard iframe URL missing"));
  }
  let txId: string | undefined;
  try {
    txId = new URL(url).searchParams.get("txId") ?? undefined;
  } catch { /* ignore malformed URL */ }
  return { url, txId };
});

export const fetchUserIp = createAsyncThunk<string>(
  "appStore/fetchUserIp",
  async () => {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip as string;
  }
);

export const inquireGCIframeResults = createAsyncThunk<
  InquireGCIframeResultsResponse,
  InquireGCIframeResultsInput | undefined
>("appStore/inquireGCIframeResults", async (body) => {
  const data = {
    url: "/cmd/credit-guard/get-iframe-result",
    body: body ?? {},
  };

  iframeDebug("inquireGCIframeResults POST", { path: data.url, body: data.body });
  const res = await postAsync(data);
  iframeDebug("inquireGCIframeResults response", res);
  return res as InquireGCIframeResultsResponse;
});
